/**
 * Derivación única del nombre del usuario — **regla del ecosistema**.
 *
 * Réplica de `AccountIdentity.normalizeName` de `quizoteca-student` (origen:
 * 2026-11-01), aplicada por admin, portal y student desde el 2026-11-02. La regla
 * vive en `specs/MODELO-DATOS.md` §USERS («Regla del ecosistema: derivación del
 * nombre»), porque este repo es la autoridad del contrato `users`:
 *
 * 1. el nombre recortado; vacío cuenta como ausente;
 * 2. si no, la parte local del correo (antes de la `@`), recortada; vacía cuenta
 *    como ausente;
 * 3. si no, el nombre fijo de la app (aquí «Profesor»);
 * 4. el resultado se trunca a 100 unidades UTF-16 sin partir una pareja de
 *    sustitutos (si la unidad 100 es un sustituto alto, se corta en 99).
 *
 * El correo no se toca. El resultado nunca es vacío y siempre cumple la regla RTDB
 * de `/classrooms/{id}/students/{uid}.name` (`length ∈ [1, 100]`).
 *
 * **Se deriva UNA vez por escritura y el mismo valor va a todos los escritores**
 * (Firebase Auth, `users.display_name` y su espejo `professor_profiles.display_name`):
 * la regla recorta antes de truncar, así que un corte que cae en un espacio deja un
 * espacio final y re-derivar ese valor no lo reproduce.
 * @see specs/DISENO.md
 */

import { USER_LIMITS } from '@/constants/limits'

/** Largo máximo del nombre en unidades UTF-16 (= `String.length`). */
export const DISPLAY_NAME_MAX_UTF16 = USER_LIMITS.DISPLAY_NAME_MAX_LENGTH

/** Nombre cuando la cuenta no tiene nombre ni correo utilizables. */
export const PROFESSOR_FALLBACK_NAME = 'Profesor'

/**
 * Recorte con `String.prototype.trim`. **Límite conocido de la réplica**, igual en
 * admin y portal: `String.trim` de Dart recorta además U+0085 (NEL) y el de
 * JavaScript no, así que un nombre rodeado de NEL conserva ese carácter aquí. No es
 * un caso real (ningún teclado lo produce) y las tres copias lo declaran igual.
 */
function presentOrNull(value: string | null | undefined): string | null {
  if (value == null) return null
  const trimmed = value.trim()
  return trimmed.length === 0 ? null : trimmed
}

function truncateUtf16(value: string): string {
  if (value.length <= DISPLAY_NAME_MAX_UTF16) return value
  let end = DISPLAY_NAME_MAX_UTF16
  const last = value.charCodeAt(end - 1)
  if (last >= 0xd800 && last <= 0xdbff) end -= 1
  return value.slice(0, end)
}

/** `true` si el nombre está ausente o en blanco tras recortar. */
export function isBlankDisplayName(value: string | null | undefined): boolean {
  return presentOrNull(value) === null
}

/**
 * Deriva el nombre a guardar a partir del nombre recibido (tecleado o el de la
 * cuenta de Google) y del correo de la cuenta.
 */
export function deriveDisplayName(
  displayName: string | null | undefined,
  email: string | null | undefined,
  fallbackName: string = PROFESSOR_FALLBACK_NAME
): string {
  const fromDisplayName = presentOrNull(displayName)
  const fromEmail = email == null ? null : presentOrNull(email.split('@')[0])
  return truncateUtf16(fromDisplayName ?? fromEmail ?? fallbackName)
}
